const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[+\d][\d\s()-]{6,}$/;
const FULL_NAME_PATTERN = /^\S+(?:\s+\S+)+$/u;

const CHECKBOX_TYPES = new Set(["checkbox", "conditions"]);
const PHONE_TYPES = new Set(["phone", "tel"]);

function normalizeFieldName(value, fallback) {
  const normalized = String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^\w-]/g, "");

  return normalized || fallback;
}

function getFallbackFieldName(type, componentIndex, isFirstTextField) {
  if (type === "email") {
    return "email";
  }

  if (CHECKBOX_TYPES.has(type)) {
    return "conditions";
  }

  if (PHONE_TYPES.has(type)) {
    return "phone";
  }

  if (type === "text" && isFirstTextField) {
    return "name";
  }

  return `field_${componentIndex + 1}`;
}

function getUniqueFieldName(requestedName, usedNames) {
  let fieldName = requestedName;
  let duplicateIndex = 2;

  while (usedNames.has(fieldName)) {
    fieldName = `${requestedName}_${duplicateIndex}`;
    duplicateIndex += 1;
  }

  usedNames.add(fieldName);
  return fieldName;
}

export function buildFieldDefinitions(components) {
  const usedNames = new Set();
  let hasTextField = false;

  return components.reduce((fields, component, componentIndex) => {
    if (component?.name !== "input") {
      return fields;
    }

    const type = String(component.type || "text").toLowerCase();
    const isFirstTextField = type === "text" && !hasTextField;

    if (isFirstTextField) {
      hasTextField = true;
    }

    const fallbackName = getFallbackFieldName(
      type,
      componentIndex,
      isFirstTextField
    );
    const requestedName = normalizeFieldName(
      component.field || component.fieldName || component.key || component.id,
      fallbackName
    );

    fields.push({
      ...component,
      componentIndex,
      fieldName: getUniqueFieldName(requestedName, usedNames),
      type,
      isCheckbox: CHECKBOX_TYPES.has(type),
    });

    return fields;
  }, []);
}

export function createInitialValues(fields) {
  return fields.reduce((values, field) => {
    values[field.fieldName] = field.isCheckbox
      ? Boolean(field.checked || field.defaultChecked)
      : field.defaultValue ?? "";

    return values;
  }, {});
}

function getRequiredFieldError(field, value, label) {
  const isEmpty = field.isCheckbox
    ? value !== true
    : String(value ?? "").trim() === "";
  const isMandatoryConditions =
    field.isCheckbox && field.type === "conditions";

  if ((!isMandatoryConditions && field.required === false) || !isEmpty) {
    return "";
  }

  return (
    field.requiredMessage ||
    (field.isCheckbox
      ? "يجب الموافقة على الشروط للمتابعة."
      : `يرجى إدخال ${label}.`)
  );
}

function getPatternError(field, value, label) {
  if (!field.pattern) {
    return "";
  }

  try {
    return new RegExp(field.pattern).test(value)
      ? ""
      : field.invalidMessage || `قيمة ${label} غير صحيحة.`;
  } catch (error) {
    console.warn(`Invalid validation pattern for ${field.fieldName}`, error);
    return "";
  }
}

export function getFieldError(field, value) {
  const label = field.label || field.placeholder || field.fieldName;
  const requiredError = getRequiredFieldError(field, value, label);

  if (requiredError || field.isCheckbox) {
    return requiredError;
  }

  const textValue = String(value ?? "").trim();

  if (!textValue) {
    return "";
  }

  if (field.fieldName === "name" && !FULL_NAME_PATTERN.test(textValue)) {
    return field.invalidMessage || "يرجى إدخال الاسم الأول واسم العائلة.";
  }

  if (field.type === "email" && !EMAIL_PATTERN.test(textValue)) {
    return field.invalidMessage || "يرجى إدخال بريد إلكتروني صحيح.";
  }

  if (PHONE_TYPES.has(field.type) && !PHONE_PATTERN.test(textValue)) {
    return field.invalidMessage || "يرجى إدخال رقم هاتف صحيح.";
  }

  if (field.minLength && textValue.length < Number(field.minLength)) {
    return (
      field.invalidMessage ||
      `يجب ألا يقل ${label} عن ${field.minLength} أحرف.`
    );
  }

  if (field.maxLength && textValue.length > Number(field.maxLength)) {
    return (
      field.invalidMessage ||
      `يجب ألا يزيد ${label} عن ${field.maxLength} أحرف.`
    );
  }

  return getPatternError(field, textValue, label);
}

export function validateFields(fields, values) {
  return fields.reduce((errors, field) => {
    const error = getFieldError(field, values[field.fieldName]);

    if (error) {
      errors[field.fieldName] = error;
    }

    return errors;
  }, {});
}

export function createSubmittedFields(fields, values) {
  return fields.reduce((submittedFields, field) => {
    const value = values[field.fieldName];
    submittedFields[field.fieldName] =
      typeof value === "string" ? value.trim() : value;

    return submittedFields;
  }, {});
}
